import React from 'react';
import marketingTheme from '../themes/marketingTheme';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { 
  Build as BuildIcon,
  GpsFixed as GpsFixedIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import { 
  FaTools, 
  FaClock, 
  FaRoute,
  FaCrosshairs,
  FaTasks,
  FaStopwatch,
  FaShieldAlt
} from 'react-icons/fa';
import { 
  MdLocationOn, 
  MdRefresh, 
  MdAssignment 
} from 'react-icons/md';
import { 
  GiToolbox, 
  GiPathDistance 
} from 'react-icons/gi';
import { 
  HiOutlineClock, 
  HiOutlineLocationMarker 
} from 'react-icons/hi';
import { 
  RiToolsLine, 
  RiTimeLine 
} from 'react-icons/ri';
import TimeMotionVisualization from '../components/TimeMotionVisualization';
import { SmartCTA } from '../components/SmartCTAs';

const TimeAndMotion = () => {
  const features = [
    {
      icon: <FaTools style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Tool-On Time',
      description: 'Visual Inference of Tool-On Time (e.g. Welder Arc-On)'
    },
    {
      icon: <FaCrosshairs style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Repositioning Time',
      description: 'Visual Inference of Repositioning Time (e.g. Time spent climbing a ladder)'
    },
    {
      icon: <MdRefresh style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Reloading Time',
      description: 'Visual Inference of Reloading Time (e.g. Time spent to reload a tool)'
    },
    {
      icon: <FaTasks style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Preparation Time',
      description: 'Visual Inference of Preparation Time (e.g. Time spent to prepare a tool)'
    },
    {
      icon: <HiOutlineClock style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Wait Time',
      description: 'Visual Inference of Wait Time (e.g. Time spent waiting for materials to arrive)'
    },
    {
      icon: <GiPathDistance style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Spaghetti Analysis',
      description: 'Visual Inference of time spent moving between workstations'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 8,
          position: 'relative',
          borderRadius: '16px',
          p: 6
        }}>
          <Typography variant="h1" sx={{ 
            color: 'white', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700
          }}>
            Time & Motion Intelligence
          </Typography>
          <Typography variant="h5" sx={{ 
            color: marketingTheme.palette.primary.main, 
            mb: 4,
            fontWeight: 400
          }}>
            Vision AI for High-Touch Workflow Improvement.
          </Typography>
        </Box>

        {/* Main Content with Case Study */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              Subtask management enables high-touch industrial workflows to be optimised through precise time-and-motion analysis.
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src="/images/worker_ergonomics.png" 
                alt="AI Vision for Time-and-Motion" 
                style={{ 
                  maxWidth: '100%',
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }} 
              />
            </Box>

            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              Time-and-motion studies are a longstanding tool for designing manufacturing processes. However, traditional approaches for time-and-motion studies are tedious and manual.
              <br /><br />
              MotionLogic® automates this process for optimisation of complex industrial workflows, transforming video into structured datasets through advanced Scene Understanding.
              <br /><br />
              Production personnel can customize recognized events through the Event Builder interface, enabling continuous process improvement powered by artificial intelligence.
              <br /><br />
              Our Scene Understanding technology transforms video into structured datasets. We present these as comprehensible reports, which can be used to inform Lean initiatives.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              p: 3,
              textAlign: 'center',
              height: 'fit-content'
            }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 4 }}>
                Hypothetical Case Study
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: '#eb4d4b', fontWeight: 'bold' }}>
                  $1B
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  COGS (of an ergonomically-intensive workflow)
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: '#1dd1a1', fontWeight: 'bold' }}>
                  5%
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  *Reduction In Waiting and Excess Motion
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
                  Yields
                </Typography>
                <Typography variant="h2" sx={{ color: '#1dd1a1', fontWeight: 'bold', mb: 2 }}>
                  $50M
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  Margin Enhancement
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                fontStyle: 'italic',
                display: 'block',
                fontSize: '0.7rem'
              }}>
                *Hypothetical, this scenario is provided for illustrative purposes only, the accuracy and applicability of this scenario may be influenced by unique factors inherent to each user's environment and practices.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Time Motion Analysis Visualization */}
        <Box sx={{ 
          my: 8,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(29, 209, 161, 0.1) 0%, rgba(26, 58, 108, 0.1) 100%)',
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 3
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
            Time Motion Process Analysis
          </Typography>
          <TimeMotionVisualization />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 2, textAlign: 'center' }}>
            AI identifies which activities add value and which don't, allowing for process optimization
          </Typography>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ 
            color: 'white', 
            textAlign: 'center', 
            mb: 6,
            fontWeight: 600
          }}>
            Inference Capability
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(237, 185, 46, 0.3)'
                  }
                }}>
                  <CardContent sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" sx={{ 
                      color: 'white', 
                      mb: 2,
                      fontWeight: 600
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6
                    }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SmartCTA
            context="experience-demo"
            fallbackHref="/contact"
            fallbackText="Learn More"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: marketingTheme.palette.primary.main,
              color: '#000',
              fontSize: '1.1rem',
              px: 4,
              py: 1.5,
              '&:hover': {
                backgroundColor: marketingTheme.palette.primary.dark,
              },
            }}
          />
        </Box>

        {/* Privacy Notice */}
        <Paper sx={{ 
          backgroundColor: 'rgba(29, 209, 161, 0.1)', 
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 4, 
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <FaShieldAlt style={{ fontSize: 60, color: '#1dd1a1', flexShrink: 0 }} />
          <Box>
            <Typography variant="h5" sx={{ 
              color: '#1dd1a1',
              fontWeight: 600,
              mb: 2
            }}>
              Pixel Privacy
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6
            }}>
              MotionLogic's camera technology preserves the privacy of individuals in visual scenes.
              We pseudonymise our data, and remove individually identifying features, including blurring faces.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TimeAndMotion;
